import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Heading,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react';

import useGetLocation from './queries/useGetLocation';
import useUpdateLocation from './queries/useUpdateLocation';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import {
    breadCrumbStyling,
    headingStyling,
} from 'components/forms/new/formStyles';
import { customTabStyles, selectedTabStyles } from '@/constants';
import {
    breadCrumbTrailEdit as breadCrumbTrail,
    editHeader as header,
    regionsAndLocationsPath,
} from './constants';
import LocationIdField from './formFields/LocationIdField';
import LocationNameField from './formFields/LocationNameField';
import LocationSwitcher from './LocationSwitcher';
import SBCManager from '../sbc/SBCManager';

/*
 * "Location Details" and "SBC" each get their own independent FormBase
 * (own Formik instance, own <form>, own Save row). They're built as sibling
 * tab panels here rather than via FormBase's tabMode, because tabMode puts
 * every tab's content inside ONE shared <form> — nesting SBCManager's own
 * FormBase inside that would be an invalid nested <form> and shows both
 * forms' Save buttons at once regardless of the active tab.
 */
const LocationEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);

    const handleBack = () => {
        dispatch({
            type: 'CHANGE_TAB_VIEW',
            payload: { level1: 1, level2: 0 },
        });
        navigate(regionsAndLocationsPath);
    };

    const {
        data: locationData,
        isLoading: locationLoading,
        isFetching: locationFetching,
        isError: notFound,
    } = useGetLocation(id);

    const handleUpdateSuccess = () => {
        toast.success('Location updated successfully!');
        handleBack();
    };

    const handleUpdateError = (error) => {
        console.error('Failed to update location:', error);
        toast.error('Failed to update location. Please try again.');
    };

    const { mutate: updateLocation, isPending: updatePending } =
        useUpdateLocation(handleUpdateSuccess, handleUpdateError);

    const finalFormSubmit = async (values) => {
        if (!values.id || !values.location) {
            console.error('Location ID and Location are required');
            return;
        }
        updateLocation({
            ...locationData,
            id: values.id,
            location: values.location,
        });
    };

    if (locationLoading || locationFetching) {
        return <LoadingIndicator />;
    }

    if (notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={{ id: '', location: '' }}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={handleBack}>
                {() => [
                    <p key="not-found">
                        This location no longer exists. It may have already been
                        deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const editInitialValues = {
        id: locationData?.id || '',
        location: locationData?.location || '',
    };

    return (
        <Box>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink as={Link} to={crumb.path}>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Heading sx={headingStyling}>{header}</Heading>
            <LocationSwitcher darkMode={darkMode} currentLocationId={id} />
            <Tabs isLazy>
                <TabList borderBottom={0} mb="10px">
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        Location Details
                    </Tab>
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        SBC
                    </Tab>
                </TabList>
                <TabPanels>
                    <TabPanel px={0} py={0}>
                        <FormBase
                            isSubmitDisabled={updatePending}
                            isLoading={updatePending}
                            initialValues={editInitialValues}
                            finalFormSubmit={finalFormSubmit}
                            backLink={handleBack}>
                            {({ errors, touched }) => [
                                <LocationIdField
                                    key="id"
                                    errors={errors}
                                    touched={touched}
                                    darkMode={darkMode}
                                    isEditMode
                                />,
                                <LocationNameField
                                    key="location"
                                    errors={errors}
                                    touched={touched}
                                    darkMode={darkMode}
                                />,
                            ]}
                        </FormBase>
                    </TabPanel>
                    <TabPanel px={0} py={0}>
                        <SBCManager locationId={id} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
};

export default LocationEdit;
